// src/RuleCreator.jsx
import { useState, useEffect, useRef } from "react";
import MiniCategoryModal from "./MiniCategoryModal";
import RuleConflictModal from "./RuleConflictModal";
import { fetchUserRules } from "./ruleMatcher";

export default function RuleCreator({
  tx,
  initialCategory,
  initialSubcategory,
  budgets,
  onSave,
  onClose,
  initialRule = null,
  mode = "create",
  accountNameById = null,
}) {
  const [category, setCategory] = useState(initialCategory || "");
  const [subcategory, setSubcategory] = useState(initialSubcategory || "");
  const [amount, setAmount] = useState("");
  const [plaidCategory, setPlaidCategory] = useState("");
  const [date, setDate] = useState("");
  const [transactionType, setTransactionType] = useState("");
  const [accountName, setAccountName] = useState("");
  const [mcc, setMcc] = useState("");
  const [amountMatchType, setAmountMatchType] = useState("exact");
  const [customTolerance, setCustomTolerance] = useState("");
  const [vendorMatchType, setVendorMatchType] = useState("exact");
  const [dateMatchType, setDateMatchType] = useState("exact");
  const [dateTolerance, setDateTolerance] = useState(""); // for ±N days
  const [dateRangeStart, setDateRangeStart] = useState("");
  const [dateRangeEnd, setDateRangeEnd] = useState("");
  const [vendorInput, setVendorInput] = useState(tx.vendor || "");

  // Prevent parent re-renders from resetting user input
  const [catTouched, setCatTouched] = useState(false);
  const [subTouched, setSubTouched] = useState(false);

  // Stable per open-target (new rule vs editing an existing rule)
  const initKey = `${mode}:${initialRule?.id || "__new__"}`;
  const didPrefillRef = useRef(null);
  const didHydrateRuleRef = useRef(null);

  // When you open a new target, allow prefill again
  useEffect(() => {
    setCatTouched(false);
    setSubTouched(false);
    didPrefillRef.current = null;
    didHydrateRuleRef.current = null;
  }, [initKey]);

  const didInitRef = useRef(null);

  // 🔧 mini modal state
  const [showMiniModal, setShowMiniModal] = useState(false);
  const [miniModalMode, setMiniModalMode] = useState(null);
  const [defaultMiniCategory, setDefaultMiniCategory] = useState("");

  // ✅ modal-level validation + conflict handling
  const [errorModal, setErrorModal] = useState({ open: false, message: "" });
  const [conflict, setConflict] = useState(null);
  const [userRules, setUserRules] = useState([]);

  // ✅ allow saving immediately after creating a new category/subcategory,
  // before budgets refresh from Firestore
  const [pendingBudgetPairs, setPendingBudgetPairs] = useState([]); 
  // shape: [{ category: "X", subcategory: "Y" }]

  useEffect(() => {
    const plaidCat = Array.isArray(tx.category) ? tx.category[0] : tx.bankCategory || "";
    setPlaidCategory(plaidCat);

    setDate(tx.date?.iso_string || tx.date?.string || "");
    setTransactionType(tx.transaction_type || "");
    const acctId = tx?.accountId || tx?.account_id || "";
    const acctLabel =
      (accountNameById && acctId && accountNameById[acctId]) ||
      tx?.accountName ||
      tx?.account_name ||
      "";

    setAccountName(acctLabel);
    setMcc(tx.merchant_category_code || "");
  }, [tx]);

  useEffect(() => {
    let mounted = true;

    fetchUserRules()
      .then((rules) => {
        if (!mounted) return;
        setUserRules(Array.isArray(rules) ? rules : []);
      })
      .catch(() => {
        // If rules can't be fetched (auth/loading), just skip conflict checking.
        if (!mounted) return;
        setUserRules([]);
      });

    return () => {
      mounted = false;
    };
  }, []);

  // ✅ Prefill category/subcategory ONCE when creating a rule (Dashboard/Transactions),
  // but never overwrite once the user starts interacting.
  useEffect(() => {
    if (initialRule) return; // edit-mode is handled by the hydrate effect below
    if (catTouched || subTouched) return;

    // Only prefill once per modal open/target
    if (didPrefillRef.current === initKey) return;
    didPrefillRef.current = initKey;

    const nextCat = initialCategory || tx?.matchedCategory || tx?.suggestedCategory || "";
    const nextSub = initialSubcategory || tx?.matchedSubcategory || tx?.suggestedSubcategory || "";

    setCategory(nextCat);
    setSubcategory(nextSub);
  }, [
    initKey,
    initialRule,
    catTouched,
    subTouched,
    initialCategory,
    initialSubcategory,
    tx?.matchedCategory,
    tx?.suggestedCategory,
    tx?.matchedSubcategory,
    tx?.suggestedSubcategory,
  ]);

  useEffect(() => {
    if (!initialRule) return;

    if (didHydrateRuleRef.current === initKey) return;
    didHydrateRuleRef.current = initKey;

    // Category/subcategory (fall back to existing initial values)
    setCategory(initialRule.category || initialCategory || "");
    setSubcategory(initialRule.subcategory || initialSubcategory || "");

    // Vendor and match settings
    setVendorInput(initialRule.vendor || tx.vendor || "");
    setVendorMatchType(initialRule.vendorMatchType || "exact");

    // Plaid category
    setPlaidCategory(
      initialRule.plaidCategory ?? (Array.isArray(tx.category) ? tx.category[0] : tx.bankCategory || "")
    );

    // Amount match
    if (typeof initialRule.amount === "number") {
      setAmount(String(Math.abs(initialRule.amount)));
    } else {
      setAmount("");
    }
    setAmountMatchType(initialRule.amountMatchType || "exact");
    setCustomTolerance(initialRule.customTolerance ? String(initialRule.customTolerance) : "");

    // Date
    setDate(initialRule.dayOfMonth ? String(initialRule.dayOfMonth) : "");
    setDateMatchType(initialRule.dateMatchType || "exact");
    setDateTolerance(initialRule.dateTolerance ? String(initialRule.dateTolerance) : "");
    setDateRangeStart(initialRule.dateRangeStart ? String(initialRule.dateRangeStart) : "");
    setDateRangeEnd(initialRule.dateRangeEnd ? String(initialRule.dateRangeEnd) : "");

    // Advanced match fields
    setTransactionType(initialRule.transactionType || tx.transactionType || "");
    setAccountName(initialRule.accountName || tx.accountName || "");
    setMcc(initialRule.mcc || tx.mcc || "");
  }, [initKey, initialRule]); // ✅ critical: do NOT depend on `tx` object anymore

  // --- Mobile breakpoint (match 768px) ---
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.matchMedia("(max-width: 768px)").matches : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(max-width: 768px)");
    const onChange = (e) => setIsMobile(e.matches);
    try { mql.addEventListener("change", onChange); } catch { mql.addListener(onChange); }
    return () => {
      try { mql.removeEventListener("change", onChange); } catch { mql.removeListener(onChange); }
    };
  }, []);

  // Mobile font sizes
  const FS_BASE  = isMobile ? 16 : 14; // default text / inputs
  const FS_HINT  = isMobile ? 14 : 12; // “This transaction: …” helper lines
  const FS_RADIO = isMobile ? 14 : 13; // radio/option rows

  // --- Mobile visual system (keeps desktop untouched) ---
  const inputStyle = {
    width: "100%",
    fontSize: FS_BASE,
    padding: "12px 12px",
    borderRadius: 12,
    border: "1px solid #ddd",
    outline: "none",
    background: "#fff",
    boxSizing: "border-box",
  };

  const selectStyle = {
    ...inputStyle,
    appearance: "none",
  };

  const cardStyle = {
    background: "#fff",
    border: "1px solid #eee",
    borderRadius: 16,
    padding: 12,
    boxShadow: "0 1px 0 rgba(0,0,0,0.03)",
  };

  const labelStyle = {
    fontWeight: 700,
    display: "block",
    marginBottom: 6,
    fontSize: 15,
    color: "#111",
  };

  const hintStyle = {
    fontSize: FS_HINT,
    color: "#6b7280",
    marginTop: 6,
    lineHeight: 1.35,
  };

  const sectionTitleStyle = {
    fontSize: 13,
    fontWeight: 800,
    color: "#6b7280",
    letterSpacing: "0.02em",
    textTransform: "uppercase",
    margin: "6px 2px 10px",
  };

  const segWrapStyle = {
    display: "inline-flex",
    gap: 6,
    flexWrap: "wrap",
    marginTop: 8,
  };

  const segBtnStyle = (active) => ({
    border: "1px solid " + (active ? "#00B074" : "#ddd"),
    background: active ? "rgba(0,176,116,0.10)" : "#fff",
    color: "#111",
    borderRadius: 999,
    padding: "8px 10px",
    fontSize: FS_RADIO,
    cursor: "pointer",
    lineHeight: 1,
  });

  // --- Safe tx formatting helpers (mobile hints use these) ---
  const txDateObj = (() => {
    try {
      if (!tx?.date) return null;
      if (typeof tx.date.toDate === "function") return tx.date.toDate();
      if (typeof tx.date.seconds === "number") return new Date(tx.date.seconds * 1000);
      if (tx.date instanceof Date) return tx.date;
      return new Date(tx.date);
    } catch { return null; }
  })();
  const txDateShort = txDateObj ? txDateObj.toLocaleDateString("en-US", { year: "2-digit", month: "numeric", day: "numeric" }) : "—";
  const txAmtAbs = typeof tx?.amount === "number" ? Math.abs(tx.amount) : null;
  const txAmtStr = txAmtAbs != null ? `$${txAmtAbs.toFixed(2)}` : "—";
  const txVendorStr = tx?.vendor || "—";
  const txPlaidCatStr = Array.isArray(tx?.category) ? (tx.category[0] || "—") : (tx?.bankCategory || "—");
  const txAcctStr = (() => {
    const acctId = tx?.accountId || tx?.account_id || "";
    const acctLabel =
      (accountNameById && acctId && accountNameById[acctId]) ||
      tx?.accountName ||
      tx?.account_name ||
      "";
    return acctLabel || "—";
  })();
  const txTypeStr = tx?.transactionType || tx?.transaction_type || "—";
  const txMccStr = tx?.mcc || tx?.merchant_category_code || "—";

  const availableCategories = budgets.map((b) => b.category);
  const availableSubcategories = budgets.find((b) => b.category === category)?.subcategories || [];
  const availablePlaidCategories = Array.isArray(tx.category) ? tx.category.filter(Boolean) : [];

  const handleSubmit = () => {
    const cleanSub = (subcategory || "").split(" (")[0];

    const norm = (v) => (v ?? "").toString().trim().toLowerCase();

    // If budgets isn't loaded yet, don't block saving.
    // (Otherwise you'll get false negatives during initial render.)
    const budgetsLoaded = Array.isArray(budgets) && budgets.length > 0;

    // Build a flexible “is active” checker that supports a few common shapes.
    const isActiveSelection = (cat, sub, budgetsArr) => {
      if (!Array.isArray(budgetsArr)) return true; // treat as loaded elsewhere
      const catN = norm(cat);
      const subN = norm(sub);
      if (!catN || !subN) return false;

      const catObj =
        budgetsArr.find((b) => norm(b.category) === catN) ||
        budgetsArr.find((b) => norm(b.categoryName) === catN) ||
        budgetsArr.find((b) => norm(b.name) === catN);

      if (!catObj) return false;

      // subcategories could be:
      // - ["Groceries", "Dining"]
      // - [{ name:"Groceries", active:true }, ...]
      // - { Groceries: {...}, Dining: {...} }
      const subs =
        catObj.subcategories ??
        catObj.subCats ??
        catObj.items ??
        catObj.subcategoryItems ??
        catObj.children ??
        null;

      if (Array.isArray(subs)) {
        // array of strings
        if (typeof subs[0] === "string") return subs.some((s) => norm(s) === subN);

        // array of objects
        return subs.some((s) => {
          const name = norm(s?.name ?? s?.label ?? s?.subcategory ?? s?.title);
          const active = s?.active ?? s?.isActive ?? true; // default true if field doesn't exist
          return name === subN && !!active;
        });
      }

      if (subs && typeof subs === "object") {
        // object map of subcategories
        return Object.keys(subs).some((k) => norm(k) === subN);
      }

      // If we can't interpret the structure, don't block users.
      return true;
    };

    // ✅ Must select a category & subcategory.
    // ✅ Only enforce "active" if budgets are loaded.
    if (!category || !cleanSub) {
      setErrorModal({
        open: true,
        message: "You must select a category and subcategory to save this rule."
      });
      return;
    }

    const isPendingPair = pendingBudgetPairs.some((p) => {
      const a = (p.category || "").trim().toLowerCase();
      const b = (p.subcategory || "").trim().toLowerCase();
      return a === norm(category) && b === norm(cleanSub);
    });

    if (budgetsLoaded && !isPendingPair && !isActiveSelection(category, cleanSub, budgets)) {
      setErrorModal({
        open: true,
        message: "You must select an active category and subcategory to save this rule."
      });
      return;
    }

    const finalAmount = amount !== "" ? Number(amount) : null;
    const finalAmountMatchType = amountMatchType;
    const finalAmountMatchValue = finalAmountMatchType === "range" ? Number(customTolerance) : 0;

    const finalDate = date !== "" ? Number(date) : null;
    const finalDateMatchType = dateMatchType;
    const finalDateMatchValue = finalDateMatchType === "range" ? Number(dateTolerance) : 0;

    const draft = {
      category,
      subcategory: cleanSub,
      vendor: vendorInput,
      plaidCategory,
      amount: finalAmount,
      transactionType,
      accountName,
      mcc,
      amountMatchType: finalAmountMatchType,
      amountMatchValue: finalAmountMatchValue,
      vendorMatchType,
      date: finalDate,
      dateMatchType: finalDateMatchType,
      dateMatchValue: finalDateMatchValue
    };

    // ✅ Conflict = same matching criteria as another rule (but different mapping)
    const criteriaKey = (r) =>
      [
        norm(r.vendor),
        norm(r.vendorMatchType || "exact"),
        norm(r.plaidCategory),
        r.amount ?? null,
        norm(r.amountMatchType || "exact"),
        Number(r.amountMatchValue || 0),
        r.date ?? null,
        norm(r.dateMatchType || "exact"),
        Number(r.dateMatchValue || 0)
      ].join("|");

    const excludeId = initialRule?.id || null;
    const hit = (userRules || []).find((r) => {
      if (!r) return false;
      if (excludeId && r.id === excludeId) return false;

      const sameCriteria = criteriaKey(r) === criteriaKey(draft);
      if (!sameCriteria) return false;

      const differentMapping =
        norm(r.category) !== norm(draft.category) || norm(r.subcategory) !== norm(draft.subcategory);

      return differentMapping;
    });

    if (hit) {
      setConflict({ existing: hit, newRule: draft });
      return;
    }

    // ✅ All good — save
    onSave(draft);
  };

  const handleMiniSave = (newCat, newSubcat) => {
    const cleanNewSub = (newSubcat || "").split(" (")[0];

    setCategory(newCat);
    setSubcategory(cleanNewSub);

    setPendingBudgetPairs((prev) => {
      const cat = (newCat || "").trim();
      const sub = (cleanNewSub || "").trim();
      if (!cat || !sub) return prev;

      const key = `${cat.toLowerCase()}|${sub.toLowerCase()}`;
      const exists = prev.some(
        (p) => `${(p.category || "").toLowerCase()}|${(p.subcategory || "").toLowerCase()}` === key
      );
      return exists ? prev : [...prev, { category: cat, subcategory: sub }];
    });

    setShowMiniModal(false);
  };

  useEffect(() => {
    const norm = (v) => (v ?? "").toString().trim().toLowerCase();
    if (!Array.isArray(budgets) || budgets.length === 0) return;

    setPendingBudgetPairs((prev) => {
      if (!prev.length) return prev;

      const hasPair = (cat, sub) => {
        const catN = norm(cat);
        const subN = norm(sub);

        const catObj =
          budgets.find((b) => norm(b.category) === catN) ||
          budgets.find((b) => norm(b.categoryName) === catN) ||
          budgets.find((b) => norm(b.name) === catN);

        if (!catObj) return false;

        const subs =
          catObj.subcategories ??
          catObj.subCats ??
          catObj.items ??
          catObj.subcategoryItems ??
          catObj.children ??
          null;

        if (Array.isArray(subs)) {
          if (typeof subs[0] === "string") return subs.some((s) => norm(s) === subN);
          return subs.some((s) => norm(s?.name ?? s?.label ?? s?.subcategory ?? s?.title) === subN);
        }

        if (subs && typeof subs === "object") {
          return Object.keys(subs).some((k) => norm(k) === subN);
        }

        return false;
      };

      // remove any pending pairs that are now present in budgets
      return prev.filter((p) => !hasPair(p.category, p.subcategory));
    });
  }, [budgets]);

  return (
    <>
      {showMiniModal && (
        <MiniCategoryModal
          isOpen={showMiniModal}
          mode={miniModalMode}
          existingCategories={availableCategories}
          defaultCategory={defaultMiniCategory}
          onSave={handleMiniSave}
          onClose={() => setShowMiniModal(false)}
        />
      )}

      {/* ✅ Error modal */}
      {errorModal.open && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 50000
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: "1.25rem",
              borderRadius: 12,
              width: "min(520px, 92vw)",
              boxShadow: "0 2px 10px rgba(0,0,0,0.25)"
            }}
          >
            <h3 style={{ marginTop: 0 }}>Can’t save rule</h3>
            <p style={{ color: "#333" }}>{errorModal.message}</p>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1rem" }}>
              <button
                onClick={() => setErrorModal({ open: false, message: "" })}
                style={{
                  backgroundColor: "#00B074",
                  color: "white",
                  border: "none",
                  padding: "0.5rem 1rem",
                  borderRadius: "6px",
                  cursor: "pointer"
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Conflict modal */}
      {conflict && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 50000
          }}
        >
          <div style={{ background: "#fff", padding: "1rem", borderRadius: 12, width: "min(560px, 92vw)" }}>
            <RuleConflictModal
              existing={conflict.existing}
              newRule={conflict.newRule}
              onViewExisting={() => {
                const id = conflict?.existing?.id;
                if (!id) return;

                const onRulesPage = (window.location.pathname || "").toLowerCase().includes("rules");
                if (onRulesPage) {
                  const el = document.getElementById(`rule-row-${id}`);
                  if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "center" });
                    el.style.outline = "2px solid #00B074";
                    el.style.outlineOffset = "-2px";
                    setTimeout(() => {
                      el.style.outline = "";
                      el.style.outlineOffset = "";
                    }, 2000);
                  }
                } else {
                  window.location.href = `/rules?focusRuleId=${encodeURIComponent(id)}`;
                }

                setConflict(null);
              }}
              onAddMore={() => setConflict(null)}
              onCancel={() => setConflict(null)}
            />
          </div>
        </div>
      )}

      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: isMobile ? "#fff" : "rgba(0, 0, 0, 0.4)",
          zIndex: 12000,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <div
          style={{
            position: isMobile ? "static" : "absolute",
            top: isMobile ? "auto" : "5%",
            left: isMobile ? "auto" : "50%",
            transform: isMobile ? "none" : "translateX(-50%)",
            backgroundColor: "white",
            borderRadius: isMobile ? "0" : "8px",
            width: isMobile ? "100%" : "600px",
            maxWidth: "100%",
            height: isMobile ? "100dvh" : "auto",
            maxHeight: isMobile ? "none" : "90%",
            overflowY: "auto",
            padding: isMobile ? "12px" : "1.5rem",
            boxShadow: isMobile ? "none" : "0 2px 12px rgba(0,0,0,0.3)",
            boxSizing: "border-box",
            fontSize: FS_BASE,
            lineHeight: 1.45,
          }}
        >
        <div
          style={{
            position: isMobile ? "sticky" : "static",
            top: 0,
            zIndex: 2,
            background: "#fff",
            padding: isMobile ? "10px 56px" : 0, // balanced left/right so text is visually centered
            borderBottom: isMobile ? "1px solid #eee" : "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          <h3 style={{ margin: 0 }}>{mode === "edit" ? "Edit Rule" : "Create New Rule"}</h3>
        </div>

        {!isMobile ? (
          // ===== Desktop: keep your existing two-column layout =====
          <div style={{ marginBottom: "1rem" }}>
            <strong>When these things happen:</strong>
            <div style={{ display: "flex", gap: "2rem" }}>
              <div style={{ flex: 1 }}>
                {/* LEFT column (unchanged) */}
                <div style={{ marginBottom: "1rem" }}></div>

                <label style={{ fontWeight: "600", display: "block", marginBottom: "0.25rem" }}>Vendor (Required)</label>
                <input value={vendorInput} onChange={(e) => setVendorInput(e.target.value)} style={{ width: "100%", fontSize: FS_BASE }} />

                <div style={{ display: "flex", gap: "1rem", marginTop: "0.25rem", color: "#444", fontSize: "0.85rem", alignItems: "center" }}>
                  {["exact", "contains", "startsWith"].map((option) => (
                    <label key={option} style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
                      <input
                        type="radio"
                        name="vendorMatchType"
                        value={option}
                        checked={vendorMatchType === option}
                        onChange={() => setVendorMatchType(option)}
                      />
                      {option === "exact" ? "Exact" : option === "contains" ? "Contains" : "Starts With"}
                    </label>
                  ))}
                </div>

                <hr style={{ margin: "0.5rem 0", borderTop: "1px solid #eee" }} />

                <label style={{ fontWeight: "600", display: "block", marginBottom: "0.25rem" }}>Plaid Category</label>
                <select value={plaidCategory} onChange={(e) => setPlaidCategory(e.target.value)} style={{ width: "100%" }}>
                  <option value="">Don't match on Plaid category</option>
                  {availablePlaidCategories.map((cat, i) => <option key={i} value={cat}>{cat}</option>)}
                </select>

                <hr style={{ margin: "0.5rem 0", borderTop: "1px solid #eee" }} />

                <label style={{ fontWeight: "600", display: "block", marginBottom: "0.25rem" }}>Amount</label>
                <input type="number" placeholder="e.g. 92.47" value={amount} onChange={(e) => setAmount(e.target.value)} style={{ width: "100%" }} />
                <div style={{ display: "flex", fontSize: "0.85rem", marginTop: "0.5rem", color: "#444", alignItems: "center", gap: "1rem" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
                    <input type="radio" name="amountMatchType" value="exact" checked={amountMatchType === "exact"} onChange={() => setAmountMatchType("exact")} />
                    Exact
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <input type="radio" name="amountMatchType" value="range" checked={amountMatchType === "range"} onChange={() => setAmountMatchType("range")} />
                    ± $
                    <input type="number" placeholder="e.g. 5" value={customTolerance} onChange={(e) => { setCustomTolerance(e.target.value); if (amountMatchType !== "range") setAmountMatchType("range"); }} style={{ width: 80 }} />
                  </label>
                </div>

                <hr style={{ margin: "0.5rem 0", borderTop: "1px solid #eee" }} />

                <label style={{ fontWeight: "600", display: "block", marginBottom: "0.25rem" }}>Day of the Month</label>
                <input type="number" min="1" max="31" placeholder="e.g. 15" value={date} onChange={(e) => setDate(e.target.value)} style={{ width: "100%" }} />
                <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginTop: "0.25rem", color: "#444", fontSize: "0.85rem" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
                    <input type="radio" name="dateMatchType" value="exact" checked={dateMatchType === "exact"} onChange={() => setDateMatchType("exact")} />
                    Exact day
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <input type="radio" name="dateMatchType" value="range" checked={dateMatchType === "range"} onChange={() => setDateMatchType("range")} />
                    ±
                    <input type="number" placeholder="e.g. 3" value={dateTolerance} onChange={(e) => { setDateTolerance(e.target.value); if (dateMatchType !== "range") setDateMatchType("range"); }} style={{ width: 60 }} />
                    days
                  </label>
                </div>

                <hr style={{ margin: "0.5rem 0", borderTop: "1px solid #eee" }} />
                <label style={{ fontWeight: "600", display: "block", marginBottom: "0.25rem" }}>Transaction Type</label>
                <input type="text" placeholder="e.g. place, digital" value={transactionType} onChange={(e) => setTransactionType(e.target.value)} style={{ width: "100%" }} />

                <hr style={{ margin: "0.5rem 0", borderTop: "1px solid #eee" }} />
                <label style={{ fontWeight: "600", display: "block", marginBottom: "0.25rem" }}>Account Name</label>
                <input type="text" value={accountName} onChange={(e) => setAccountName(e.target.value)} style={{ width: "100%" }} />

                <hr style={{ margin: "0.5rem 0", borderTop: "1px solid #eee" }} />
                <label style={{ fontWeight: "600", display: "block", marginBottom: "0.25rem" }}>Merchant Category Code (MCC)</label>
                <input type="text" value={mcc} onChange={(e) => setMcc(e.target.value)} style={{ width: "100%" }} />
              </div>

              <div style={{ flex: 1, fontSize: "0.85rem", color: "#555" }}>
                <strong style={{ display: "block", marginBottom: "0.5rem" }}>This transaction was:</strong>
                <table style={{ width: "100%", tableLayout: "fixed" }}>
                  <tbody>
                    <tr><td><strong>Amount:</strong></td><td>{txAmtStr}</td></tr>
                    <tr><td><strong>Date:</strong></td><td>{txDateShort}</td></tr>
                    <tr><td><strong>Plaid Category:</strong></td><td>{txPlaidCatStr}</td></tr>
                    <tr><td><strong>Vendor:</strong></td><td>{txVendorStr}</td></tr>
                    <tr><td><strong>Account:</strong></td><td>{txAcctStr}</td></tr>
                    <tr><td><strong>Type:</strong></td><td>{txTypeStr}</td></tr>
                    <tr><td><strong>MCC:</strong></td><td>{txMccStr}</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          // ===== Mobile: cleaner, card-based flow =====
          <div style={{ marginBottom: 10, display: "grid", gap: 12 }}>
            <div style={sectionTitleStyle}>When these things happen</div>

            {/* Vendor */}
            <div style={cardStyle}>
              <label style={labelStyle}>Vendor</label>
              <input
                value={vendorInput}
                onChange={(e) => setVendorInput(e.target.value)}
                style={inputStyle}
              />
              <div style={hintStyle}>This transaction: {txVendorStr}</div>

              <div style={segWrapStyle}>
                <button type="button" style={segBtnStyle(vendorMatchType === "exact")} onClick={() => setVendorMatchType("exact")}>
                  Exact
                </button>
                <button type="button" style={segBtnStyle(vendorMatchType === "contains")} onClick={() => setVendorMatchType("contains")}>
                  Contains
                </button>
                <button type="button" style={segBtnStyle(vendorMatchType === "startsWith")} onClick={() => setVendorMatchType("startsWith")}>
                  Starts
                </button>
              </div>
            </div>

            {/* Plaid Category */}
            <div style={cardStyle}>
              <label style={labelStyle}>Plaid category</label>
              <select value={plaidCategory} onChange={(e) => setPlaidCategory(e.target.value)} style={selectStyle}>
                <option value="">Don’t match on Plaid</option>
                {availablePlaidCategories.map((cat, i) => (
                  <option key={i} value={cat}>{cat}</option>
                ))}
              </select>
              <div style={hintStyle}>This transaction: {txPlaidCatStr}</div>
            </div>

            {/* Amount */}
            <div style={cardStyle}>
              <label style={labelStyle}>Amount</label>
              <input
                type="number"
                placeholder="e.g. 92.47"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                style={inputStyle}
              />

              <div style={segWrapStyle}>
                <button type="button" style={segBtnStyle(amountMatchType === "exact")} onClick={() => setAmountMatchType("exact")}>
                  Exact
                </button>
                <button type="button" style={segBtnStyle(amountMatchType === "range")} onClick={() => setAmountMatchType("range")}>
                  Range
                </button>
              </div>

              {amountMatchType === "range" && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ ...hintStyle, marginTop: 0 }}>Tolerance</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                    <div style={{ color: "#6b7280" }}>± $</div>
                    <input
                      type="number"
                      placeholder="e.g. 5"
                      value={customTolerance}
                      onChange={(e) => setCustomTolerance(e.target.value)}
                      style={{ ...inputStyle, width: 140, padding: "10px 12px" }}
                    />
                  </div>
                </div>
              )}

              <div style={hintStyle}>This transaction: {txAmtStr}</div>
            </div>

            {/* Date */}
            <div style={cardStyle}>
              <label style={labelStyle}>Day of month</label>
              <input
                type="number"
                min="1"
                max="31"
                placeholder="e.g. 15"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                style={inputStyle}
              />

              <div style={segWrapStyle}>
                <button type="button" style={segBtnStyle(dateMatchType === "exact")} onClick={() => setDateMatchType("exact")}>
                  Exact
                </button>
                <button type="button" style={segBtnStyle(dateMatchType === "range")} onClick={() => setDateMatchType("range")}>
                  Range
                </button>
              </div>

              {dateMatchType === "range" && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ ...hintStyle, marginTop: 0 }}>Tolerance</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                    <div style={{ color: "#6b7280" }}>±</div>
                    <input
                      type="number"
                      placeholder="e.g. 3"
                      value={dateTolerance}
                      onChange={(e) => setDateTolerance(e.target.value)}
                      style={{ ...inputStyle, width: 140, padding: "10px 12px" }}
                    />
                    <div style={{ color: "#6b7280" }}>days</div>
                  </div>
                </div>
              )}

              <div style={hintStyle}>This transaction: {txDateShort}</div>
            </div>

            {/* Advanced (collapsed) */}
            <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
              <details>
                <summary
                  style={{
                    listStyle: "none",
                    cursor: "pointer",
                    padding: 12,
                    fontWeight: 800,
                    color: "#111",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  More match options
                  <span style={{ color: "#6b7280", fontWeight: 700, fontSize: 13 }}>Optional</span>
                </summary>

                <div style={{ padding: 12, borderTop: "1px solid #eee", display: "grid", gap: 12 }}>
                  <div>
                    <label style={labelStyle}>Type</label>
                    <input
                      type="text"
                      placeholder="e.g. place, digital"
                      value={transactionType}
                      onChange={(e) => setTransactionType(e.target.value)}
                      style={inputStyle}
                    />
                    <div style={hintStyle}>This transaction: {txTypeStr}</div>
                  </div>

                  <div>
                    <label style={labelStyle}>Account</label>
                    <input
                      type="text"
                      value={accountName}
                      onChange={(e) => setAccountName(e.target.value)}
                      style={inputStyle}
                    />
                    <div style={hintStyle}>This transaction: {txAcctStr}</div>
                  </div>

                  <div>
                    <label style={labelStyle}>MCC</label>
                    <input
                      type="text"
                      value={mcc}
                      onChange={(e) => setMcc(e.target.value)}
                      style={inputStyle}
                    />
                    <div style={hintStyle}>This transaction: {txMccStr}</div>
                  </div>
                </div>
              </details>
            </div>
          </div>
        )}

        <hr style={{ margin: "1rem 0", borderTop: "1px solid #eee" }} />
        <div style={{ marginBottom: "1rem" }}></div>
        
        <div style={{ marginBottom: "1rem" }}>
          <div style={isMobile ? sectionTitleStyle : undefined}>
            {isMobile ? "Match it to a budget category" : "Then match this transaction to:"}
          </div>

          <div style={isMobile ? cardStyle : undefined}>
            <div style={{ marginBottom: 12 }}>
              <label style={isMobile ? labelStyle : undefined}>Category</label>
              {!isMobile && <br />}
              <select
                value={category}
                onChange={(e) => {
                  if (e.target.value === "__new__") {
                    setMiniModalMode("category");
                    setShowMiniModal(true);
                  } else {
                    setCatTouched(true);
                    setSubTouched(true);
                    setCategory(e.target.value);
                    setSubcategory("");
                  }
                }}
                style={isMobile ? selectStyle : { width: "100%" }}
              >
                <option value="">Select</option>
                {[...new Set([...availableCategories, category])].map((cat, i) => (
                  <option key={i} value={cat}>{cat}</option>
                ))}
                <option value="__new__">➕ Add New Category</option>
              </select>
            </div>

            <div>
              <label style={isMobile ? labelStyle : undefined}>Subcategory</label>
              {!isMobile && <br />}
              <select
                value={subcategory}
                onChange={(e) => {
                  if (e.target.value === "__new__") {
                    setMiniModalMode("subcategory");
                    setDefaultMiniCategory(category);
                    setShowMiniModal(true);
                  } else {
                    setSubTouched(true);
                    setSubcategory(e.target.value);
                  }
                }}
                style={isMobile ? selectStyle : { width: "100%" }}
              >
                <option value="">Select</option>
                {[...new Set([...availableSubcategories.map((s) => s.name), subcategory])]
                  .filter(Boolean)
                  .map((name, i) => (
                    <option key={i} value={name}>{name}</option>
                  ))}
                {category && <option value="__new__">➕ Add New Subcategory</option>}
              </select>
            </div>
          </div>
        </div>

        <div
          style={{
            position: isMobile ? "sticky" : "static",
            bottom: 0,
            background: "#fff",
            padding: isMobile ? "12px 0 8px" : 0,
            borderTop: isMobile ? "1px solid #eee" : "none",
            marginTop: isMobile ? 12 : "1rem",
            display: "flex",
            gap: 10,
            justifyContent: "space-between",
          }}
        >
          <button
            onClick={onClose}
            style={{
              flex: isMobile ? 1 : "unset",
              backgroundColor: "#111",
              color: "white",
              border: "none",
              padding: isMobile ? "12px 14px" : "0.5rem 1rem",
              borderRadius: isMobile ? 12 : "6px",
              cursor: "pointer",
              fontWeight: 800,
            }}
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            style={{
              flex: isMobile ? 1 : "unset",
              backgroundColor: "#00B074",
              color: "white",
              border: "none",
              padding: isMobile ? "12px 14px" : "0.5rem 1rem",
              borderRadius: isMobile ? 12 : "6px",
              cursor: "pointer",
              fontWeight: 800,
            }}
          >
            Save rule
          </button>
        </div>
      </div>
      </div>
    </>
  );
}